import { useBulkViewFilters } from '@/components/bulkQueue/BulkTableContext';
import { UseSetSearchParamReturn, useSetSearchParam } from '@/hooks/router';
import { ALL_VISIT_COUNTS } from '@/pages/AllVisitsPage/AllVisits.gql';
import {
    AllVisitCountsQuery,
    AllVisitCountsQueryVariables,
} from '@/pages/AllVisitsPage/AllVisits.gql.gql.types';
import { DEFAULT_TAB, TAB_CONFIGS } from '@/pages/AllVisitsPage/AllVisitsPage.constants';
import { ALL_VISITS_TAB, AllVisitsPageNS } from '@/pages/AllVisitsPage/AllVisitsPage.types';
import {
    getDefaultSelectedSecondaryTabs,
    parseSecondaryTabsFromUrl,
} from '@/pages/AllVisitsPage/AllVisitsPage.utils';
import { VIEW_FILTERS } from '@/pages/AllVisitsPage/viewFilters';
import { useQuery } from '@apollo/client/react';
import { VisitFilter, VisitState, VisitWorkflowStep } from 'gql-types';
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef } from 'react';

export const usePrimaryTabFromSearchParam =
    (): UseSetSearchParamReturn<AllVisitsPageNS.PrimaryTab> => {
        const [selectedTab, setSelectedTab] =
            useSetSearchParam<AllVisitsPageNS.PrimaryTab>('status');

        return [selectedTab, setSelectedTab];
    };

type UsePrimaryTab = () => [
    tab: AllVisitsPageNS.TabConfig,
    setTab: Dispatch<AllVisitsPageNS.TabConfig>,
];

export const usePrimaryTab: UsePrimaryTab = () => {
    const [selectedTab, setSelectedTab] = usePrimaryTabFromSearchParam();

    const tab = useMemo(() => {
        if (selectedTab !== null) {
            const found = TAB_CONFIGS.find((tab) => tab.tab === selectedTab);
            if (!found) {
                setSelectedTab(null);
                return DEFAULT_TAB;
            }

            return found;
        }

        return DEFAULT_TAB;
    }, [selectedTab, setSelectedTab]);

    const setTab = useCallback(
        (nextTab: AllVisitsPageNS.TabConfig) => {
            setSelectedTab(nextTab.tab);
        },
        [setSelectedTab],
    );

    return [tab, setTab];
};

type UseCurrentSecondaryTabs = () => [
    tabs: VisitWorkflowStep[],
    setTabs: Dispatch<SetStateAction<VisitWorkflowStep[]>>,
];

export const useCurrentSecondaryTabs: UseCurrentSecondaryTabs = () => {
    const [primaryTab] = usePrimaryTab();
    const [secondaryParam, setSecondaryParam] = useSetSearchParam('filters');

    const defaultTabs = useMemo(
        () => getDefaultSelectedSecondaryTabs(primaryTab.lowerTabs),
        [primaryTab.lowerTabs],
    );
    const searchParamsSecondaryTabs = useMemo(
        () => parseSecondaryTabsFromUrl(secondaryParam),
        [secondaryParam],
    );

    const validSearchParamTabs = useMemo(() => {
        return searchParamsSecondaryTabs.filter((tab) => {
            if (!primaryTab.lowerTabs) {
                return false;
            }
            return Array.from(primaryTab.lowerTabs).some(([tabTitle]) => tabTitle === tab);
        });
    }, [primaryTab.lowerTabs, searchParamsSecondaryTabs]);

    const selectedTabs = useMemo(() => {
        if (!secondaryParam) {
            return defaultTabs;
        }
        return validSearchParamTabs.length > 0 ? validSearchParamTabs : defaultTabs;
    }, [secondaryParam, validSearchParamTabs, defaultTabs]);

    const setSecondaryParamRef = useRef(setSecondaryParam);
    setSecondaryParamRef.current = setSecondaryParam;

    useEffect(() => {
        if (!secondaryParam) {
            return;
        }
        if (validSearchParamTabs.length !== searchParamsSecondaryTabs.length) {
            const encoded =
                validSearchParamTabs.length > 0
                    ? validSearchParamTabs.map(encodeURIComponent).join(',')
                    : null;
            setSecondaryParamRef.current(encoded, true);
        }
    }, [secondaryParam, validSearchParamTabs, searchParamsSecondaryTabs.length]);

    const setTabs = useCallback(
        (param: SetStateAction<VisitWorkflowStep[]>) => {
            const currentTabs = parseSecondaryTabsFromUrl(secondaryParam);
            const newTabs = typeof param === 'function' ? param(currentTabs) : param;

            const encoded = newTabs.length > 0 ? newTabs.map(encodeURIComponent).join(',') : null;
            setSecondaryParam(encoded, true);
        },
        [secondaryParam, setSecondaryParam],
    );

    useEffect(() => {
        if (!primaryTab.lowerTabs) {
            setSecondaryParam(null, true);
        }
    }, [primaryTab.lowerTabs, setSecondaryParam]);

    return [selectedTabs, setTabs];
};

const getTotalCount = (param?: { totalCount: number | null } | null): number =>
    typeof param?.totalCount === 'number' ? param.totalCount : 0;

const composeFilter = (base: VisitFilter, viewFilter: VisitFilter): VisitFilter => ({
    ...base,
    ...viewFilter,
    // The dialog's explicit outcome filter (carried on `base`) overrides the
    // view's default `outcome: NONE` rule on the Needs tabs, so the count
    // badges agree with what the list query renders.
    ...(base.outcome ? { outcome: base.outcome } : {}),
});

export const useCounts = () => {
    const { visitFilters } = useBulkViewFilters({});

    const variables = useMemo(
        (): AllVisitCountsQueryVariables => ({
            all: composeFilter(visitFilters, VIEW_FILTERS[ALL_VISITS_TAB]),
            needsServices: composeFilter(
                visitFilters,
                VIEW_FILTERS[VisitWorkflowStep.NEEDS_SERVICE],
            ),
            needsClinician: composeFilter(
                visitFilters,
                VIEW_FILTERS[VisitWorkflowStep.NEEDS_CLINICIAN],
            ),
            needsDate: composeFilter(visitFilters, VIEW_FILTERS[VisitWorkflowStep.NEEDS_DATE]),
            unscheduled: composeFilter(visitFilters, VIEW_FILTERS.UNSCHEDULED),
            scheduled: composeFilter(visitFilters, VIEW_FILTERS.SCHEDULED),
            confirmed: composeFilter(visitFilters, VIEW_FILTERS.CONFIRMED),
            enRoute: composeFilter(visitFilters, VIEW_FILTERS.EN_ROUTE),
            atLocation: composeFilter(visitFilters, VIEW_FILTERS.AT_LOCATION),
            inReview: composeFilter(visitFilters, VIEW_FILTERS.IN_REVIEW),
            ended: composeFilter(visitFilters, VIEW_FILTERS.ENDED),
        }),
        [visitFilters],
    );

    const { data } = useQuery<AllVisitCountsQuery, AllVisitCountsQueryVariables>(ALL_VISIT_COUNTS, {
        variables,
        fetchPolicy: 'cache-and-network',
    });

    return useMemo(
        (): Record<AllVisitsPageNS.PrimaryTab, number | null> => ({
            [ALL_VISITS_TAB]: getTotalCount(data?.all),
            [VisitState.UNSCHEDULED]: getTotalCount(data?.unscheduled),
            [VisitState.SCHEDULED]: getTotalCount(data?.scheduled),
            [VisitState.EN_ROUTE]: getTotalCount(data?.enRoute),
            [VisitState.IN_REVIEW]: getTotalCount(data?.inReview),
            [VisitState.ENDED]: getTotalCount(data?.ended),
            [VisitState.AT_LOCATION]: getTotalCount(data?.atLocation),
            [VisitState.CONFIRMED]: getTotalCount(data?.confirmed),
            [VisitWorkflowStep.NEEDS_SERVICE]: getTotalCount(data?.needsServices),
            [VisitWorkflowStep.NEEDS_DATE]: getTotalCount(data?.needsDate),
            [VisitWorkflowStep.NEEDS_CLINICIAN]: getTotalCount(data?.needsClinician),
        }),
        [data],
    );
};
